import { useSets } from '@/hooks/useSets';
import { useTasks } from '@/hooks/useTasks';
import { GENERAL_SET } from '@/constants/sets';
import type { Set } from '@/types/set';

interface UseEffectiveSetListResult {
  /** The effective list of sets (General prepended if unassigned moves exist) */
  sets: Set[];
  /** Whether either the sets or tasks data is still loading */
  isLoading: boolean;
  /** Error from either query, if any */
  error: Error | null;
}

/**
 * Computes the "effective" set list for display in the Sets view.
 *
 * If there are active moves with no set_id (unassigned moves), a virtual
 * "General" set is prepended to the list. Both SetList and SetsPage consume
 * this hook to stay in sync on what the first selectable set is.
 */
export function useEffectiveSetList(): UseEffectiveSetListResult {
  const { data: setsData, isLoading: setsLoading, error: setsError } = useSets('active');
  const { data: tasksData, isLoading: tasksLoading, error: tasksError } = useTasks('active');

  const isLoading = setsLoading || tasksLoading;
  const error = (setsError as Error | null) || (tasksError as Error | null);

  if (isLoading || !setsData || !tasksData) {
    return { sets: [], isLoading, error };
  }

  const hasUnassignedTasks = tasksData.tasks.some((task) => task.set_id === null);
  const realSets = setsData.sets;

  const sets: Set[] = hasUnassignedTasks ? [GENERAL_SET, ...realSets] : realSets;

  return { sets, isLoading: false, error };
}
